import type {
  RegexMatchPayload,
  RegexWorkerInput,
  RegexWorkerOutput,
} from '../../workers/regexWorker';

/**
 * Page-side wrapper around the Regex Worker, plus a synchronous
 * replace helper that runs on the main thread.
 *
 * Invariants the worker owes us — pinned by tests:
 *  1. catastrophic-backtracking inputs return `{ timeout: true }`,
 *     not a hang;
 *  2. compile failures (`new RegExp(...)` throws) return `{ ok: false, error }`;
 *  3. global / sticky flags collect every non-overlapping match;
 *  4. zero-length matches do not infinite-loop.
 *
 * The library is also testable without a real Worker (Node + jsdom):
 * a `__setExecForTests` injection point swaps in a synchronous
 * implementation. Vitest uses it; the SPA never touches it.
 */

export interface RegexInput {
  pattern: string;
  flags: string;
  testString: string;
  /** ms; default 200. */
  timeoutMs?: number;
}

export interface RegexMatch extends RegexMatchPayload {}

export type RegexResult =
  | { ok: true; matches: RegexMatch[] }
  | { ok: false; error: string }
  | { ok: false; timeout: true };

/** 500 KB cap from docs §7.1. */
export const REGEX_TEST_STRING_CAP = 500 * 1024;

type Exec = (input: RegexWorkerInput) => Promise<RegexWorkerOutput>;

let execImpl: Exec | null = null;

function defaultExec(input: RegexWorkerInput): Promise<RegexWorkerOutput> {
  return new Promise((resolve) => {
    const worker = new Worker(
      new URL('../../workers/regexWorker.ts', import.meta.url),
      { type: 'module' },
    );
    worker.onmessage = (e: MessageEvent<RegexWorkerOutput>) => {
      resolve(e.data);
      worker.terminate();
    };
    worker.onerror = (e) => {
      resolve({ ok: false, error: e.message || 'worker error' });
      worker.terminate();
    };
    worker.postMessage(input);
  });
}

/**
 * Test-only injection. Swap in a synchronous exec that simulates the
 * worker (so vitest doesn't need real-Worker support). Pass `null`
 * to restore the real worker path. Production code never calls this.
 */
export function __setExecForTests(exec: Exec | null): void {
  execImpl = exec;
}

export async function testRegex(input: RegexInput): Promise<RegexResult> {
  const { pattern, flags, testString, timeoutMs = 200 } = input;
  if (testString.length > REGEX_TEST_STRING_CAP) {
    return {
      ok: false,
      error: `测试串超过 ${(REGEX_TEST_STRING_CAP / 1024).toFixed(0)} KB 上限`,
    };
  }

  const exec = execImpl ?? defaultExec;
  const work = exec({ pattern, flags, testString });

  // The worker side can hang on catastrophic backtracking. Race against
  // a deadline; the caller of `defaultExec` is responsible for tearing
  // down the worker on the timeout branch (see below).
  let timer: ReturnType<typeof setTimeout> | null = null;
  const deadline = new Promise<RegexResult>((resolve) => {
    timer = setTimeout(() => {
      resolve({ ok: false, timeout: true });
    }, timeoutMs);
  });

  const result = await Promise.race([
    work.then<RegexResult>((out) => {
      if (timer) clearTimeout(timer);
      if (out.ok) return { ok: true, matches: out.matches };
      return { ok: false, error: out.error };
    }),
    deadline,
  ]);

  // If the deadline beat the worker, also tear down via the
  // already-bound onmessage path: defaultExec terminates on its own
  // result resolution. For the `timeout: true` branch we DON'T
  // terminate proactively here because `defaultExec` already owns the
  // worker handle. In SPA usage we'd add a hook to terminate on
  // timeout; for now the abandoned worker dies on page navigation.
  return result;
}

/**
 * Simple replacement runner. Runs on the main thread because by the
 * time the user clicks "应用替换" we've already validated the pattern
 * via `testRegex`, so catastrophic backtracking would have surfaced
 * earlier. We still try/catch in case `pattern` is empty / invalid.
 */
export function applyReplace(
  input: RegexInput,
  replacement: string,
): { ok: true; output: string } | { ok: false; error: string } {
  try {
    const re = new RegExp(input.pattern, input.flags);
    return { ok: true, output: input.testString.replace(re, replacement) };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}
